import React, {
  useCallback, useEffect, useState,
} from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import SyncSolicitudDocConfirmModal from 'screens/Admin/Accounts/components/SyncSolicitudDocConfirmModal';
import SyncSolicitudDocInitialErrorModal from 'screens/Admin/Accounts/components/SyncSolicitudDocInitialErrorModal';
import SyncSolicitudDocLogsModal from 'screens/Admin/Accounts/components/SyncSolicitudDocLogsModal';
import SyncSolicitudDocNeverSyncedModal from 'screens/Admin/Accounts/components/SyncSolicitudDocNeverSyncedModal';
import SyncSolicitudDocProgressModal from 'screens/Admin/Accounts/components/SyncSolicitudDocProgressModal';
import SyncSolicitudDocResultModal from 'screens/Admin/Accounts/components/SyncSolicitudDocResultModal';
import {
  isNeverSyncedNotFound,
  isTerminalSyncStatus,
  runIdFromApiResponse,
  statusFromApiResponse,
} from 'screens/Admin/Accounts/components/syncSolicitudDocModalHelpers';

export type SyncSolicitudDocFlowProps = {
  accountId: number | null;
  onExited: () => void;
  sincronizarSolicitudDocAction: ( id: number, opts?: { silent?: boolean } ) => Promise<any>;
  getSyncStatusAction: (
    id: number,
    opts?: { runId?: string; silent?: boolean },
  ) => Promise<any>;
  getSyncLogsAction: ( id: number, params?: any, opts?: { silent?: boolean } ) => Promise<any>;
};

type Phase =
  | 'loadingInitial'
  | 'neverSynced'
  | 'confirm'
  | 'progress'
  | 'result'
  | 'initialError';

function SyncSolicitudDocFlowInner( {
  accountId,
  onExited,
  sincronizarSolicitudDocAction,
  getSyncStatusAction,
  getSyncLogsAction,
}: Omit<SyncSolicitudDocFlowProps, 'accountId'> & { accountId: number } ) {
  const [phase, setPhase] = useState<Phase>( 'loadingInitial' );
  const [runId, setRunId] = useState<string | null>( null );
  const [terminalRes, setTerminalRes] = useState<any>( null );
  const [initialErrorRes, setInitialErrorRes] = useState<any>( null );
  const [timedOut, setTimedOut] = useState( false );
  const [triggerBusy, setTriggerBusy] = useState( false );
  const [logsOpen, setLogsOpen] = useState( false );
  const [logsResponse, setLogsResponse] = useState<any>( null );
  const [logsRunId, setLogsRunId] = useState<string | null>( null );
  const [logsShowRaw, setLogsShowRaw] = useState( true );

  useEffect( () => {
    let cancelled = false;
    const load = async () => {
      setPhase( 'loadingInitial' );
      setRunId( null );
      setTerminalRes( null );
      setInitialErrorRes( null );
      setTimedOut( false );
      const res = await getSyncStatusAction( accountId, { silent: true } );
      if ( cancelled ) return;
      if ( isNeverSyncedNotFound( res ) ) {
        setPhase( 'neverSynced' );
        return;
      }
      if ( !res.ok ) {
        setInitialErrorRes( res );
        setPhase( 'initialError' );
        return;
      }
      const st = statusFromApiResponse( res );
      const rid = runIdFromApiResponse( res );
      if ( st === 'RUNNING' && rid ) {
        setRunId( rid );
        setPhase( 'progress' );
        return;
      }
      if ( isTerminalSyncStatus( st ) ) {
        setTerminalRes( res );
        if ( rid ) setRunId( rid );
        setPhase( 'result' );
        return;
      }
      setPhase( 'confirm' );
    };
    load().catch( () => {} );
    return () => {
      cancelled = true;
    };
  }, [accountId, getSyncStatusAction] );

  const runTrigger = useCallback( async () => {
    setTriggerBusy( true );
    try {
      const tr = await sincronizarSolicitudDocAction( accountId, { silent: true } );
      const rid = runIdFromApiResponse( tr );
      if ( rid ) {
        setRunId( rid );
        setTimedOut( false );
        setTerminalRes( null );
        setPhase( 'progress' );
        return;
      }
      setTerminalRes( tr );
      setTimedOut( false );
      setPhase( 'result' );
    } finally {
      setTriggerBusy( false );
    }
  }, [accountId, sincronizarSolicitudDocAction] );

  const handleProgressTerminal = useCallback( ( res: any ) => {
    setTerminalRes( res );
    setTimedOut( false );
    const rid = runIdFromApiResponse( res );
    if ( rid ) setRunId( rid );
    setPhase( 'result' );
  }, [] );

  const handleProgressTimeout = useCallback( () => {
    setTimedOut( true );
    setTerminalRes( null );
    setPhase( 'result' );
  }, [] );

  const effectiveRunId = runId ?? runIdFromApiResponse( terminalRes );

  const openErrorLogs = useCallback( async () => {
    const rid = effectiveRunId;
    if ( !rid ) return;
    const logsRes = await getSyncLogsAction(
      accountId,
      {
        runId: rid,
        onlyErrors: true,
        page: 1,
        size: 50,
      },
      { silent: true },
    );
    setLogsResponse( logsRes );
    setLogsRunId( rid );
    setLogsShowRaw( false );
    setLogsOpen( true );
  }, [accountId, effectiveRunId, getSyncLogsAction] );

  const closeLogs = useCallback( () => {
    setLogsOpen( false );
    setLogsResponse( null );
    setLogsRunId( null );
  }, [] );

  const terminalStatusForLogs = timedOut
    ? 'FAILED'
    : statusFromApiResponse( terminalRes );
  const apiFailed = Boolean( terminalRes && !terminalRes.ok );
  const canRetry = Boolean(
    timedOut
    || isTerminalSyncStatus( terminalStatusForLogs )
    || apiFailed,
  );
  const allowErrorLogs = Boolean(
    effectiveRunId
    && ( timedOut
      || terminalStatusForLogs === 'FAILED'
      || terminalStatusForLogs === 'COMPLETED_WITH_ERRORS'
      || apiFailed ),
  );

  return (
    <>
      <Modal show={phase === 'loadingInitial'} centered backdrop="static">
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" />
        </Modal.Body>
      </Modal>

      <SyncSolicitudDocNeverSyncedModal
        show={phase === 'neverSynced'}
        accountId={accountId}
        isStarting={triggerBusy}
        onClose={onExited}
        onStartSync={runTrigger}
      />

      <SyncSolicitudDocConfirmModal
        show={phase === 'confirm'}
        accountId={accountId}
        isConfirming={triggerBusy}
        onClose={onExited}
        onConfirm={runTrigger}
      />

      {phase === 'progress' && runId ? (
        <SyncSolicitudDocProgressModal
          show
          accountId={accountId}
          runId={runId}
          getSyncStatusAction={getSyncStatusAction}
          onTerminal={handleProgressTerminal}
          onTimeout={handleProgressTimeout}
          onClose={onExited}
        />
      ) : null}

      <SyncSolicitudDocResultModal
        show={phase === 'result'}
        accountId={accountId}
        terminalResponse={terminalRes}
        timedOut={timedOut}
        runId={effectiveRunId}
        isRetrying={triggerBusy}
        onClose={onExited}
        onRetry={canRetry ? runTrigger : undefined}
        onViewErrorLogs={allowErrorLogs ? openErrorLogs : undefined}
      />

      <SyncSolicitudDocInitialErrorModal
        show={phase === 'initialError'}
        response={initialErrorRes}
        onClose={onExited}
      />

      <SyncSolicitudDocLogsModal
        show={logsOpen}
        selectedAccountId={accountId}
        runId={logsRunId}
        logsResponse={logsResponse}
        showRawFooter={logsShowRaw}
        onClose={closeLogs}
      />
    </>
  );
}

function SyncSolicitudDocFlow( {
  accountId,
  onExited,
  sincronizarSolicitudDocAction,
  getSyncStatusAction,
  getSyncLogsAction,
}: SyncSolicitudDocFlowProps ) {
  if ( accountId == null ) return null;
  return (
    <SyncSolicitudDocFlowInner
      key={accountId}
      accountId={accountId}
      onExited={onExited}
      sincronizarSolicitudDocAction={sincronizarSolicitudDocAction}
      getSyncStatusAction={getSyncStatusAction}
      getSyncLogsAction={getSyncLogsAction}
    />
  );
}

export default SyncSolicitudDocFlow;
